import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { api, Group, Player } from "../api/client";
import {
  MeasurementType,
  NormativeExercise,
  NormativeTest,
  normativesApi,
  ResultInput,
} from "../api/normatives";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./NormativesPage.css";

// "2026-09-12" → "12.09.2026", from the string: new Date() would shift the day
// west of Greenwich.
function formatDate(value: string): string {
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

const TYPE_LABELS: Record<MeasurementType, string> = {
  count: "Количество",
  made_of: "Попадания из попыток",
  time_distance: "Время или расстояние",
};

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");
const byOrder = (a: NormativeExercise, b: NormativeExercise) =>
  a.sort_order - b.sort_order || a.name.localeCompare(b.name, "ru");

function testTitle(t: NormativeTest): string {
  return t.title ? `Сдача ${formatDate(t.date)} — ${t.title}` : `Сдача ${formatDate(t.date)}`;
}

type ExerciseForm = {
  name: string;
  type: MeasurementType;
  unit: string;
  better: "higher" | "lower";
  order: string;
};

const EMPTY_EXERCISE: ExerciseForm = { name: "", type: "count", unit: "", better: "higher", order: "" };

type Cell = { primary: string; secondary: string };
const cellKey = (playerID: string, exerciseID: string) => `${playerID}|${exerciseID}`;
const numberText = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));

// Coaches type decimals with a comma on Russian keyboards.
function parseNumber(text: string): number | null | undefined {
  const trimmed = text.trim().replace(",", ".");
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

function checkCell(exercise: NormativeExercise, cell: Cell): { input?: Omit<ResultInput, "player_id" | "exercise_id">; problem?: string } {
  const primary = parseNumber(cell.primary);
  const secondary = exercise.measurement_type === "made_of" ? parseNumber(cell.secondary) : null;
  if (primary === undefined || secondary === undefined) return { problem: "нужно число" };
  if (primary === null) {
    if (secondary !== null) return { problem: "не указано, сколько попал" };
    return { input: { value_primary: null, value_secondary: null } };
  }
  if (primary < 0) return { problem: "результат не может быть отрицательным" };
  if (exercise.measurement_type !== "time_distance" && !Number.isInteger(primary)) {
    return { problem: "нужно целое число" };
  }
  if (secondary !== null) {
    if (secondary <= 0 || !Number.isInteger(secondary)) return { problem: "попыток должно быть целое число больше нуля" };
    if (primary > secondary) return { problem: "попаданий больше, чем попыток" };
  }
  return { input: { value_primary: primary, value_secondary: secondary } };
}

export default function NormativesPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [exercises, setExercises] = useState<NormativeExercise[]>([]);
  const [groupID, setGroupID] = useState("");
  const [tests, setTests] = useState<NormativeTest[]>([]);
  const [testsLoading, setTestsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newDate, setNewDate] = useState("");
  const [newTitle, setNewTitle] = useState("");

  const [exerciseForm, setExerciseForm] = useState<ExerciseForm>(EMPTY_EXERCISE);
  const [editingExercise, setEditingExercise] = useState<NormativeExercise | null>(null);

  const [openTest, setOpenTest] = useState<NormativeTest | null>(null);
  const [exerciseID, setExerciseID] = useState("");
  const [cells, setCells] = useState<Record<string, Cell>>({});
  const [saved, setSaved] = useState(false);

  const latestTests = useRef(0);
  const { confirm, dialog } = useConfirm();

  const loadExercises = useCallback(async () => {
    const list = await normativesApi.listExercises();
    setExercises((list ?? []).slice().sort(byOrder));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [groupList, playerList] = await Promise.all([api.listGroups(), api.listPlayers(), loadExercises()]);
        const sorted = (groupList ?? []).slice().sort(byName);
        setGroups(sorted);
        setPlayers((playerList ?? []).slice().sort(byName));
        setGroupID(sorted[0]?.id ?? "");
      } catch (err: any) {
        console.error("Failed to load normatives", err);
        setError(err?.message || "Не удалось загрузить нормативы");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadExercises]);

  const loadTests = useCallback(async () => {
    if (!groupID) return;
    const loadID = ++latestTests.current;
    setTestsLoading(true);
    try {
      const list = await normativesApi.listTests(groupID);
      if (loadID !== latestTests.current) return;
      setTests(list ?? []);
    } catch (err: any) {
      if (loadID !== latestTests.current) return;
      console.error("Failed to load normative tests", err);
      setTests([]);
      setError(err?.message || "Не удалось загрузить сдачи");
    } finally {
      if (loadID === latestTests.current) setTestsLoading(false);
    }
  }, [groupID]);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  const run = async (action: () => Promise<unknown>, failure: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err: any) {
      console.error("Normatives action failed", err);
      setError(err?.message || failure);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const showTest = (test: NormativeTest) => {
    const values: Record<string, Cell> = {};
    for (const r of test.results ?? []) {
      values[cellKey(r.player_id, r.exercise_id)] = {
        primary: numberText(r.value_primary),
        secondary: numberText(r.value_secondary),
      };
    }
    setOpenTest(test);
    setCells(values);
    setSaved(false);
    setExerciseID((current) => current || exercises.find((e) => e.is_active)?.id || "");
  };

  const open = (id: string) =>
    run(async () => showTest(await normativesApi.getTest(id)), "Не удалось открыть сдачу");

  const createTest = async (e: FormEvent) => {
    e.preventDefault();
    if (!newDate || !groupID) return;
    await run(async () => {
      const created = await normativesApi.createTest({
        group_id: groupID,
        date: newDate,
        title: newTitle.trim() || null,
      });
      setNewDate("");
      setNewTitle("");
      loadTests();
      showTest(await normativesApi.getTest(created.id));
    }, "Не удалось создать сдачу");
  };

  const removeTest = async (test: NormativeTest) => {
    const confirmed = await confirm({
      title: `Удалить сдачу ${formatDate(test.date)}?`,
      body: (
        <>
          <p>
            Удалятся все результаты этой сдачи. Дети и родители перестанут видеть их в приложении,
            из графиков прогресса они тоже пропадут.
          </p>
          <p>Отменить удаление нельзя.</p>
        </>
      ),
      confirmLabel: "Удалить сдачу",
      danger: true,
    });
    if (!confirmed) return;
    await run(() => normativesApi.deleteTest(test.id), "Не удалось удалить сдачу");
    await loadTests();
  };

  const original = new Map<string, Cell>();
  for (const r of openTest?.results ?? []) {
    original.set(cellKey(r.player_id, r.exercise_id), {
      primary: numberText(r.value_primary),
      secondary: numberText(r.value_secondary),
    });
  }
  const cellOf = (key: string): Cell => cells[key] ?? { primary: "", secondary: "" };
  const isChanged = (key: string) => {
    const now = cellOf(key);
    const was = original.get(key) ?? { primary: "", secondary: "" };
    return now.primary.trim() !== was.primary || now.secondary.trim() !== was.secondary;
  };

  // Children of the group, plus anyone who already has a result in this test
  // (moved to another group or deactivated since), so no saved value is hidden.
  const testPlayers = (() => {
    if (!openTest) return [];
    const withResults = new Set((openTest.results ?? []).map((r) => r.player_id));
    return players.filter((p) => (p.is_active && p.group_id === openTest.group_id) || withResults.has(p.id));
  })();

  const testExercises = (() => {
    if (!openTest) return [];
    const withResults = new Set((openTest.results ?? []).map((r) => r.exercise_id));
    return exercises.filter((e) => e.is_active || withResults.has(e.id));
  })();

  const dirtyKeys = Object.keys(cells).filter(isChanged);

  const setCell = (key: string, change: Partial<Cell>) => {
    setCells((c) => ({ ...c, [key]: { ...cellOf(key), ...c[key], ...change } }));
    setSaved(false);
  };

  const saveResults = async () => {
    if (!openTest) return;
    const payload: ResultInput[] = [];
    const problems: string[] = [];
    for (const exercise of testExercises) {
      for (const p of testPlayers) {
        const key = cellKey(p.id, exercise.id);
        if (!isChanged(key)) continue;
        const { input, problem } = checkCell(exercise, cellOf(key));
        if (problem) problems.push(`${exercise.name}, ${p.name}: ${problem}`);
        else if (input) payload.push({ player_id: p.id, exercise_id: exercise.id, ...input });
      }
    }
    if (problems.length > 0) {
      setError(problems.join("; "));
      return;
    }
    if (payload.length === 0) return;
    const ok = await run(async () => {
      showTest(await normativesApi.saveResults(openTest.id, payload));
    }, "Не удалось сохранить результаты");
    if (ok) setSaved(true);
  };

  const closeTest = async () => {
    if (dirtyKeys.length > 0) {
      const confirmed = await confirm({
        title: "Закрыть сдачу без сохранения?",
        body: <p>Введённые и не сохранённые результаты пропадут.</p>,
        confirmLabel: "Закрыть без сохранения",
        danger: true,
      });
      if (!confirmed) return;
    }
    setOpenTest(null);
    setCells({});
    setError(null);
  };

  const startEditExercise = (exercise: NormativeExercise) => {
    setEditingExercise(exercise);
    setExerciseForm({
      name: exercise.name,
      type: exercise.measurement_type,
      unit: exercise.unit_label,
      better: exercise.higher_is_better ? "higher" : "lower",
      order: String(exercise.sort_order),
    });
  };

  const resetExerciseForm = () => {
    setEditingExercise(null);
    setExerciseForm(EMPTY_EXERCISE);
  };

  const submitExercise = async (e: FormEvent) => {
    e.preventDefault();
    const name = exerciseForm.name.trim();
    if (!name) return;
    const fields = {
      name,
      unit_label: exerciseForm.type === "made_of" ? "" : exerciseForm.unit.trim(),
      higher_is_better: exerciseForm.better === "higher",
      sort_order: Number.parseInt(exerciseForm.order, 10) || 0,
    };
    const ok = await run(async () => {
      if (editingExercise) {
        await normativesApi.updateExercise(editingExercise.id, fields);
      } else {
        await normativesApi.createExercise({ ...fields, measurement_type: exerciseForm.type });
      }
      await loadExercises();
    }, editingExercise ? "Не удалось сохранить упражнение" : "Не удалось добавить упражнение");
    if (ok) resetExerciseForm();
  };

  const setArchived = (exercise: NormativeExercise, archived: boolean) =>
    run(async () => {
      await normativesApi.updateExercise(exercise.id, { is_active: !archived });
      await loadExercises();
    }, "Не удалось сохранить упражнение");

  if (loading) return <PageLoader />;

  const currentExercise = testExercises.find((e) => e.id === exerciseID) ?? testExercises[0];
  const filledCount = (id: string) => (openTest?.results ?? []).filter((r) => r.exercise_id === id).length;

  return (
    <div className="page page-wide">
      <h2>Нормативы</h2>
      <p className="page-description">
        Контрольные упражнения и сдачи по группам. Результаты видят дети и родители в приложении.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <label className="cp-field nt-group">
        <span>Группа</span>
        <select
          className="mav-input"
          value={groupID}
          disabled={busy || openTest !== null}
          onChange={(e) => {
            setGroupID(e.target.value);
            setError(null);
          }}
        >
          {groups.length === 0 && <option value="">Групп нет</option>}
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </label>

      {openTest ? (
        <section className="nt-section" aria-labelledby="nt-test-title">
          <div className="nt-section-header">
            <h3 id="nt-test-title">{testTitle(openTest)}</h3>
            <button className="mav-btn-secondary" disabled={busy} onClick={closeTest}>
              К списку сдач
            </button>
          </div>

          {testExercises.length === 0 ? (
            <div className="mav-card nt-empty">Упражнений нет — добавьте их ниже.</div>
          ) : (
            <>
              <label className="cp-field nt-exercise-pick">
                <span>Упражнение</span>
                <select
                  className="mav-input"
                  value={currentExercise?.id ?? ""}
                  disabled={busy}
                  onChange={(e) => setExerciseID(e.target.value)}
                >
                  {testExercises.map((ex) => (
                    <option key={ex.id} value={ex.id}>
                      {filledCount(ex.id) > 0 ? `${ex.name} (заполнено: ${filledCount(ex.id)})` : ex.name}
                    </option>
                  ))}
                </select>
              </label>

              <div className="table-wrap">
                <table className="settings-table nt-results-table">
                  <thead>
                    <tr>
                      <th>Ребёнок</th>
                      <th>{currentExercise?.measurement_type === "made_of" ? "Попал из попыток" : "Результат"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testPlayers.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="empty-cell">
                          В группе нет детей
                        </td>
                      </tr>
                    ) : (
                      currentExercise &&
                      testPlayers.map((p) => {
                        const key = cellKey(p.id, currentExercise.id);
                        const cell = cellOf(key);
                        return (
                          <tr key={p.id} className={isChanged(key) ? "nt-row-changed" : undefined}>
                            <td className="nt-cell-player">{p.name}</td>
                            <td>
                              {currentExercise.measurement_type === "made_of" ? (
                                <span className="nt-made-of">
                                  <input
                                    className="mav-input nt-number"
                                    inputMode="numeric"
                                    aria-label={`Попал: ${p.name}`}
                                    value={cell.primary}
                                    disabled={busy}
                                    onChange={(e) => setCell(key, { primary: e.target.value })}
                                  />
                                  <span className="nt-unit">из</span>
                                  <input
                                    className="mav-input nt-number"
                                    inputMode="numeric"
                                    aria-label={`Попыток: ${p.name}`}
                                    value={cell.secondary}
                                    disabled={busy}
                                    onChange={(e) => setCell(key, { secondary: e.target.value })}
                                  />
                                </span>
                              ) : (
                                <span className="nt-made-of">
                                  <input
                                    className="mav-input nt-number"
                                    inputMode="decimal"
                                    aria-label={`Результат: ${p.name}`}
                                    value={cell.primary}
                                    disabled={busy}
                                    onChange={(e) => setCell(key, { primary: e.target.value })}
                                  />
                                  {currentExercise.unit_label && (
                                    <span className="nt-unit">{currentExercise.unit_label}</span>
                                  )}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="nt-save">
                <button
                  className="mav-btn-primary"
                  disabled={busy || dirtyKeys.length === 0}
                  onClick={saveResults}
                >
                  Сохранить результаты
                </button>
                {saved && dirtyKeys.length === 0 ? (
                  <span className="mav-status mav-status-ok" role="status">
                    Результаты сохранены
                  </span>
                ) : (
                  dirtyKeys.length > 0 && (
                    <span className="mav-status mav-status-dirty">Изменено ячеек: {dirtyKeys.length}</span>
                  )
                )}
              </div>
              <p className="mav-hint">
                Изменения по всем упражнениям сохраняются одной кнопкой. Пустая ячейка стирает
                результат. После сохранения детям и их родителям приходит уведомление.
              </p>
            </>
          )}
        </section>
      ) : (
        <section className="nt-section" aria-labelledby="nt-tests-title">
          <h3 id="nt-tests-title">Сдачи нормативов</h3>

          <form className="mav-card cp-form nt-test-form" aria-label="Новая сдача" onSubmit={createTest}>
            <label className="cp-field">
              <span>Дата</span>
              <input
                type="date"
                className="mav-input"
                value={newDate}
                disabled={busy || !groupID}
                onChange={(e) => setNewDate(e.target.value)}
              />
            </label>
            <label className="cp-field">
              <span>Название</span>
              <input
                className="mav-input"
                value={newTitle}
                maxLength={100}
                placeholder="Необязательно"
                disabled={busy || !groupID}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </label>
            <div className="cp-actions">
              <button type="submit" className="mav-btn-primary" disabled={busy || !groupID || !newDate}>
                Создать сдачу
              </button>
            </div>
          </form>

          <div className="table-wrap">
            <table className="settings-table nt-tests-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Название</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tests.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="empty-cell">
                      {testsLoading ? "Загрузка…" : "Сдач пока нет"}
                    </td>
                  </tr>
                ) : (
                  tests.map((t) => (
                    <tr key={t.id}>
                      <td className="date-cell">{formatDate(t.date)}</td>
                      <td className="value-text">{t.title || "—"}</td>
                      <td className="cell-action">
                        <div className="row-actions">
                          <button className="mav-btn-secondary" disabled={busy} onClick={() => open(t.id)}>
                            Открыть
                          </button>
                          <button className="btn-quiet-danger" disabled={busy} onClick={() => removeTest(t)}>
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="nt-section" aria-labelledby="nt-exercises-title">
        <h3 id="nt-exercises-title">Упражнения</h3>

        <form
          className="mav-card cp-form nt-exercise-form"
          aria-label={editingExercise ? "Изменение упражнения" : "Новое упражнение"}
          onSubmit={submitExercise}
        >
          <label className="cp-field">
            <span>Название</span>
            <input
              className="mav-input"
              value={exerciseForm.name}
              maxLength={100}
              disabled={busy}
              onChange={(e) => setExerciseForm({ ...exerciseForm, name: e.target.value })}
            />
          </label>
          <div className="cp-field">
            <label className="cp-field">
              <span>Как измеряется</span>
              <select
                className="mav-input"
                value={exerciseForm.type}
                disabled={busy || editingExercise !== null}
                onChange={(e) => setExerciseForm({ ...exerciseForm, type: e.target.value as MeasurementType })}
              >
                {(Object.keys(TYPE_LABELS) as MeasurementType[]).map((type) => (
                  <option key={type} value={type}>
                    {TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
            </label>
            {editingExercise && (
              <p className="mav-hint">Способ измерения не меняется: сохранённые результаты читались бы по-другому.</p>
            )}
          </div>
          <div className="cp-field">
            <label className="cp-field">
              <span>Единица</span>
              <input
                className="mav-input"
                value={exerciseForm.type === "made_of" ? "" : exerciseForm.unit}
                maxLength={20}
                placeholder={exerciseForm.type === "made_of" ? "" : "раз, сек, см"}
                disabled={busy || exerciseForm.type === "made_of"}
                onChange={(e) => setExerciseForm({ ...exerciseForm, unit: e.target.value })}
              />
            </label>
            {exerciseForm.type === "made_of" && (
              <p className="mav-hint">Не нужна: результат показывается как «7 из 10».</p>
            )}
          </div>
          <label className="cp-field">
            <span>Что лучше</span>
            <select
              className="mav-input"
              value={exerciseForm.better}
              disabled={busy}
              onChange={(e) => setExerciseForm({ ...exerciseForm, better: e.target.value as ExerciseForm["better"] })}
            >
              <option value="higher">Больше</option>
              <option value="lower">Меньше</option>
            </select>
          </label>
          <label className="cp-field">
            <span>Порядок в списке</span>
            <input
              type="number"
              className="mav-input"
              value={exerciseForm.order}
              step={1}
              placeholder="0"
              disabled={busy}
              onChange={(e) => setExerciseForm({ ...exerciseForm, order: e.target.value })}
            />
          </label>
          <div className="cp-actions row-actions nt-form-actions">
            <button type="submit" className="mav-btn-primary" disabled={busy || !exerciseForm.name.trim()}>
              {editingExercise ? "Сохранить" : "Добавить упражнение"}
            </button>
            {editingExercise && (
              <button type="button" className="mav-btn-secondary" disabled={busy} onClick={resetExerciseForm}>
                Отмена
              </button>
            )}
          </div>
        </form>

        <div className="table-wrap">
          <table className="settings-table nt-exercises-table">
            <thead>
              <tr>
                <th>Упражнение</th>
                <th>Как измеряется</th>
                <th>Что лучше</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {exercises.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-cell">
                    Упражнений пока нет
                  </td>
                </tr>
              ) : (
                exercises.map((ex) => (
                  <tr key={ex.id} className={ex.is_active ? undefined : "nt-row-archived"}>
                    <td className="nt-cell-exercise">{ex.name}</td>
                    <td>
                      {ex.unit_label && ex.measurement_type !== "made_of"
                        ? `${TYPE_LABELS[ex.measurement_type]}, ${ex.unit_label}`
                        : TYPE_LABELS[ex.measurement_type] ?? ex.measurement_type}
                    </td>
                    <td className="nt-cell-better">{ex.higher_is_better ? "больше — лучше" : "меньше — лучше"}</td>
                    <td className="cell-action">
                      <div className="row-actions">
                        {!ex.is_active && <span className="mav-badge mav-badge-muted">В архиве</span>}
                        <button className="mav-btn-secondary" disabled={busy} onClick={() => startEditExercise(ex)}>
                          Изменить
                        </button>
                        <button
                          className="mav-btn-secondary"
                          disabled={busy}
                          onClick={() => setArchived(ex, ex.is_active)}
                        >
                          {ex.is_active ? "В архив" : "Вернуть"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="mav-hint nt-archive-hint">
          Упражнение из архива не предлагается в новых сдачах, старые результаты по нему остаются.
        </p>
      </section>

      {dialog}
    </div>
  );
}
